/**
 * <rs-photo-builder>: the photo charm builder (blocks/rs-photo-builder.liquid).
 *
 * Steps: 1 pick a photo, 2 position it inside the charm's shape (drag, pinch/slider zoom, rotate), 3 confirm.
 * Add to cart is held back (with a message) until all three are done.
 *
 * The frame's shape and proportions come from the first heart/circle area in <rs-photo-preview> (the same numbers that
 * place the preview on the product image). What is inside the frame is drawn to a canvas and attached to the product
 * form as properties[Photo]; the full photo goes along as properties[_Original photo]. Shopify uploads both when the
 * item is added (the product form posts FormData), so no app is involved. The cropped photo is also shown on the
 * product image through window.rsPhotoPreview (assets/rs-photo-preview.js).
 */

const HEART =
  'M0.5,1 C0.26,0.84 0,0.64 0,0.31 C0,0.12 0.14,0 0.3,0 C0.4,0 0.47,0.04 0.5,0.1 C0.53,0.04 0.6,0 0.7,0 C0.86,0 1,0.12 1,0.31 C1,0.64 0.74,0.84 0.5,1 Z';
// Both files upload with Add to cart, so they are kept small: the photo on a charm is about 2 cm across (a sharp print
// needs roughly 250-300 px), so 900 px is still 3x that; the original is only a backup for re-cropping.
const OUT_LONG_EDGE = 900; // pixels of the printed crop
const OUT_QUALITY = 0.86;
const ORIGINAL_LONG_EDGE = 1000; // pixels of the kept original
const ORIGINAL_QUALITY = 0.8;
const LOW_RES = 450; // photo pixels across the frame below which we warn
const MAX_ZOOM = 4;

/** @param {HTMLCanvasElement} canvas @param {string} name @param {number} quality @returns {Promise<File>} */
const canvasFile = (canvas, name, quality) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(new File([blob], name, { type: 'image/jpeg' })) : reject(new Error('Could not save photo'))),
      'image/jpeg',
      quality
    )
  );

/** @param {HTMLInputElement} input @param {File | null} file */
function setFile(input, file) {
  const transfer = new DataTransfer();
  if (file) transfer.items.add(file);
  input.files = transfer.files;
}

class RsPhotoBuilder extends HTMLElement {
  /** @type {HTMLImageElement | null} */ #img = null;
  #shape = 'heart';
  /** @type {number[][] | null} the window's traced outline (percent of its box), when the product has one */
  #outline = null;
  #aspect = 1;
  #zoom = 1;
  #rot = 0;
  #ox = 0;
  #oy = 0;
  #frame = { x: 0, y: 0, w: 0, h: 0, size: 0 };
  #dirty = false;
  #saving = /** @type {Promise<void> | null} */ (null);
  #saveTimer = 0;
  #pointers = new Map();
  #pinch = { dist: 0, zoom: 1 };
  #photoName = 'photo';
  /** counts photos, so each photo is added to the cart automatically only once */
  #photoId = 0;
  #autoAddedFor = -1;
  #needed = false;
  #uploading = false;
  /** true for the one submit the builder sends itself after the newest crop is saved */
  #resubmit = false;

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    // The charm's frame: the first heart/circle area measured for this product
    try {
      const areas = JSON.parse(this.dataset.areas || document.querySelector('rs-photo-preview')?.getAttribute('data-areas') || '[]');
      const main = areas.find((a) => a.shape === 'heart' || a.shape === 'circle');
      if (main) {
        this.#shape = main.shape;
        this.#aspect = main.w / main.h;
        if (Array.isArray(main.pts) && main.pts.length > 2) this.#outline = main.pts;
      }
    } catch {
      /* default: heart, 1:1 */
    }

    this.fileInput = /** @type {HTMLInputElement} */ (this.querySelector('[data-rpb-file]'));
    this.drop = /** @type {HTMLElement} */ (this.querySelector('[data-rpb-drop]'));
    this.stage = /** @type {HTMLElement} */ (this.querySelector('[data-rpb-stage]'));
    this.photo = /** @type {HTMLImageElement} */ (this.querySelector('[data-rpb-photo]'));
    this.mask = /** @type {SVGSVGElement} */ (this.querySelector('[data-rpb-mask]'));
    this.zoomInput = /** @type {HTMLInputElement} */ (this.querySelector('[data-rpb-zoom]'));
    this.confirm = /** @type {HTMLInputElement} */ (this.querySelector('[data-rpb-confirm]'));
    this.out = /** @type {HTMLInputElement} */ (this.querySelector('[data-rpb-out]'));
    this.original = /** @type {HTMLInputElement} */ (this.querySelector('[data-rpb-original]'));
    this.status = /** @type {HTMLElement} */ (this.querySelector('[data-rpb-status]'));

    this.fileInput.addEventListener('change', () => {
      const file = this.fileInput.files?.[0];
      if (file) this.#load(file);
    });
    for (const type of ['dragenter', 'dragover']) {
      this.drop.addEventListener(type, (event) => {
        event.preventDefault();
        this.drop.classList.add('is-over');
      });
    }
    for (const type of ['dragleave', 'drop']) {
      this.drop.addEventListener(type, () => this.drop.classList.remove('is-over'));
    }
    this.drop.addEventListener('drop', (event) => {
      event.preventDefault();
      const file = [...(event.dataTransfer?.files || [])].find((f) => f.type.startsWith('image/'));
      if (file) this.#load(file);
    });

    this.zoomInput.addEventListener('input', () => this.#setZoom(Number(this.zoomInput.value)));
    this.querySelector('[data-rpb-zoom-in]')?.addEventListener('click', () => this.#setZoom(this.#zoom + 0.25));
    this.querySelector('[data-rpb-zoom-out]')?.addEventListener('click', () => this.#setZoom(this.#zoom - 0.25));
    this.querySelector('[data-rpb-rotate]')?.addEventListener('click', () => {
      this.#rot = (this.#rot + 90) % 360;
      this.#clamp();
      this.#render(true);
    });
    this.querySelector('[data-rpb-reset]')?.addEventListener('click', () => {
      this.#zoom = 1;
      this.#rot = 0;
      this.#ox = 0;
      this.#oy = 0;
      this.#render(true);
    });

    this.stage.addEventListener('pointerdown', this.#onDown);
    this.stage.addEventListener('pointermove', this.#onMove);
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) this.stage.addEventListener(type, this.#onUp);
    this.stage.addEventListener(
      'wheel',
      (event) => {
        if (!this.#img) return;
        event.preventDefault();
        this.#setZoom(this.#zoom * (event.deltaY < 0 ? 1.08 : 1 / 1.08));
      },
      { passive: false }
    );
    this.stage.addEventListener('keydown', this.#onKey);

    // Ticking the confirm box is the last step: the charm is added to the cart by itself (once per photo)
    this.confirm?.addEventListener('change', () => {
      this.#update();
      if (this.confirm?.checked) this.#autoAdd();
    });

    const engraving = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-rpb-engraving]'));
    const count = this.querySelector('[data-rpb-count]');
    // Only sent when something is typed, so the order has no empty "Engraving" line
    const engravingName = engraving?.getAttribute('name') || '';
    const syncEngraving = () => {
      if (!engraving) return;
      if (count) count.textContent = String(engraving.value.length);
      if (engraving.value.trim()) engraving.setAttribute('name', engravingName);
      else engraving.removeAttribute('name');
    };
    engraving?.addEventListener('input', syncEngraving);
    syncEngraving();

    new ResizeObserver(() => this.#img && this.#layout()).observe(this.stage);

    // Hold Add to cart back until the builder is complete. The theme handles submit with a capture listener on
    // document (assets/component.js), so this one sits on window, which runs before it.
    window.addEventListener('submit', this.#onSubmit, true);
    this.#update();
  }

  disconnectedCallback() {
    window.removeEventListener('submit', this.#onSubmit, true);
  }

  get form() {
    return /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId || ''));
  }

  get ready() {
    return Boolean(this.#img && this.confirm?.checked && this.out.files?.length);
  }

  /** @param {File} file */
  #load(file) {
    const error = /** @type {HTMLElement} */ (this.querySelector('[data-rpb-file-error]'));
    error.hidden = true;
    if (!file.type.startsWith('image/')) {
      error.textContent = 'Please choose a photo (JPG or PNG).';
      error.hidden = false;
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      this.#img = img;
      this.#photoName = (file.name || 'photo').replace(/\.[^.]+$/, '').slice(0, 40) || 'photo';
      this.#photoId += 1;
      this.#zoom = 1;
      this.#rot = 0;
      this.#ox = 0;
      this.#oy = 0;
      this.photo.src = url;
      if (this.confirm) this.confirm.checked = false;

      const title = this.querySelector('[data-rpb-drop-title]');
      const hint = this.querySelector('[data-rpb-drop-hint]');
      if (title) title.textContent = 'Photo added';
      if (hint) hint.textContent = `${file.name || 'Your photo'} · tap to replace`;
      const icon = this.querySelector('.rpb__drop-icon');
      if (icon) icon.innerHTML = `<img src="${url}" alt="">`;

      this.querySelector('[data-rpb-stage-card]')?.removeAttribute('hidden');
      this.querySelector('[data-rpb-engraving-card]')?.removeAttribute('hidden');
      this.querySelector('[data-rpb-confirm-row]')?.removeAttribute('hidden');
      this.#layout();
      this.#render(true);
      await this.#saveOriginal(img);
      this.stage.focus({ preventScroll: true });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      error.textContent = 'This photo could not be opened. Please choose a JPG or PNG.';
      error.hidden = false;
    };
    img.src = url;
  }

  /** Size the frame inside the stage and redraw its outline. */
  #layout() {
    const size = this.stage.clientWidth;
    const inner = size * 0.84;
    const w = this.#aspect >= 1 ? inner : inner * this.#aspect;
    const h = this.#aspect >= 1 ? inner / this.#aspect : inner;
    this.#frame = { x: (size - w) / 2, y: (size - h) / 2, w, h, size };

    const { x, y } = this.#frame;
    const shapePath = this.#outline
      ? `M${this.#outline.map(([px, py]) => `${(x + (px / 100) * w).toFixed(2)},${(y + (py / 100) * h).toFixed(2)}`).join(' L')} Z`
      : this.#shape === 'circle'
        ? `M${x},${y + h / 2} a${w / 2},${h / 2} 0 1,0 ${w},0 a${w / 2},${h / 2} 0 1,0 ${-w},0 Z`
        : HEART.replace(/(-?[\d.]+),(-?[\d.]+)/g, (_, px, py) => `${(x + Number(px) * w).toFixed(2)},${(y + Number(py) * h).toFixed(2)}`);
    this.mask.setAttribute('viewBox', `0 0 ${size} ${size}`);
    this.mask.innerHTML = `
      <path d="M0,0H${size}V${size}H0Z ${shapePath}" fill="rgba(255,255,255,0.74)" fill-rule="evenodd"/>
      <path d="${shapePath}" fill="none" stroke="#fff" stroke-width="6"/>
      <path d="${shapePath}" fill="none" stroke="#b9bcc6" stroke-width="2.5"/>
      <path d="${shapePath}" fill="none" stroke="#e11d48" stroke-width="1.2" stroke-dasharray="5 5"/>`;
    this.#clamp();
    this.#render(false);
  }

  get #rotated() {
    const img = /** @type {HTMLImageElement} */ (this.#img);
    return this.#rot % 180 ? { w: img.naturalHeight, h: img.naturalWidth } : { w: img.naturalWidth, h: img.naturalHeight };
  }

  /** Photo pixels to screen pixels: the photo always covers the frame, times the zoom. */
  get #scale() {
    const r = this.#rotated;
    return Math.max(this.#frame.w / r.w, this.#frame.h / r.h) * this.#zoom;
  }

  #clamp() {
    if (!this.#img) return;
    const r = this.#rotated;
    const s = this.#scale;
    const maxX = Math.max(0, (r.w * s - this.#frame.w) / 2);
    const maxY = Math.max(0, (r.h * s - this.#frame.h) / 2);
    this.#ox = Math.min(maxX, Math.max(-maxX, this.#ox));
    this.#oy = Math.min(maxY, Math.max(-maxY, this.#oy));
  }

  /** @param {number} zoom */
  #setZoom(zoom) {
    if (!this.#img) return;
    this.#zoom = Math.min(MAX_ZOOM, Math.max(1, zoom));
    this.#clamp();
    this.#render(true);
  }

  /** @param {boolean} changed  true when the crop changed (saves a new crop shortly after) */
  #render(changed) {
    const img = this.#img;
    if (!img) return;
    const s = this.#scale;
    const cx = this.#frame.size / 2 + this.#ox;
    const cy = this.#frame.size / 2 + this.#oy;
    this.photo.style.width = `${img.naturalWidth}px`;
    this.photo.style.height = `${img.naturalHeight}px`;
    this.photo.style.transform = `translate(${cx}px, ${cy}px) rotate(${this.#rot}deg) scale(${s}) translate(${-img.naturalWidth / 2}px, ${-img.naturalHeight / 2}px)`;
    this.zoomInput.value = String(this.#zoom);

    const lowres = this.querySelector('[data-rpb-lowres]');
    if (lowres) lowres.toggleAttribute('hidden', this.#frame.w / s >= LOW_RES);

    if (changed) {
      this.#dirty = true;
      if (this.confirm?.checked) this.confirm.checked = false; // moved after confirming: confirm again
      this.#update();
      clearTimeout(this.#saveTimer);
      this.#saveTimer = window.setTimeout(() => this.#saveCrop(), 350);
    }
  }

  /** Draws what is inside the frame and attaches it to the form (and shows it on the product image). */
  #saveCrop() {
    const run = async () => {
      const img = this.#img;
      if (!img) return;
      this.#dirty = false;
      const k = OUT_LONG_EDGE / Math.max(this.#frame.w, this.#frame.h);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(this.#frame.w * k);
      canvas.height = Math.round(this.#frame.h * k);
      const ctx = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingQuality = 'high';
      ctx.translate(canvas.width / 2 + this.#ox * k, canvas.height / 2 + this.#oy * k);
      ctx.rotate((this.#rot * Math.PI) / 180);
      const s = this.#scale * k;
      ctx.drawImage(img, (-img.naturalWidth * s) / 2, (-img.naturalHeight * s) / 2, img.naturalWidth * s, img.naturalHeight * s);
      const file = await canvasFile(canvas, `${this.#photoName}-${this.#shape}.jpg`, OUT_QUALITY);
      setFile(this.out, file);
      /** @type {any} */ (window).rsPhotoPreview?.(file);
      this.#update();
    };
    this.#saving = (this.#saving || Promise.resolve()).then(run, run);
    return this.#saving;
  }

  /** @param {HTMLImageElement} img */
  async #saveOriginal(img) {
    const k = Math.min(1, ORIGINAL_LONG_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * k);
    canvas.height = Math.round(img.naturalHeight * k);
    const ctx = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    try {
      setFile(this.original, await canvasFile(canvas, `${this.#photoName}-original.jpg`, ORIGINAL_QUALITY));
    } catch (error) {
      console.warn(error);
    }
  }

  /** Steps, progress bar, status line and the Add to cart look. */
  #update() {
    if (this.#uploading) return;
    const hasPhoto = Boolean(this.#img);
    const confirmed = Boolean(this.confirm?.checked);
    const steps = this.querySelectorAll('[data-rpb-step]');
    const state = [hasPhoto ? 'done' : 'on', hasPhoto ? (confirmed ? 'done' : 'on') : '', confirmed ? 'done' : hasPhoto ? 'on' : ''];
    steps.forEach((el, i) => {
      el.classList.toggle('is-on', state[i] !== '');
      el.classList.toggle('is-done', state[i] === 'done');
    });
    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-rpb-progress]'));
    if (bar) bar.style.width = confirmed ? '100%' : hasPhoto ? '62%' : '10%';

    const ready = this.ready;
    this.status.classList.toggle('is-ready', ready);
    this.status.textContent = ready
      ? 'Ready. Your photo is attached to this charm.'
      : hasPhoto
        ? 'Position your photo, then tick the box and it is added to your cart.'
        : 'Add your photo to continue.';
    if (ready && this.#needed) {
      this.#needed = false;
      this.classList.remove('is-needed');
    }
    this.form?.closest('product-form-component')?.querySelector('.product-form-buttons')?.classList.toggle('rpb-waiting', !ready);
  }

  /** After confirming: wait for the newest crop, then press Add to cart for them. */
  async #autoAdd() {
    const photo = this.#photoId;
    if (this.#autoAddedFor === photo || this.#uploading) return;
    this.#autoAddedFor = photo;
    await new Promise((resolve) => setTimeout(resolve, 450)); // let them see the tick
    if (this.#saving) await this.#saving;
    // they may have unticked or picked another photo in the meantime
    if (!this.confirm?.checked || photo !== this.#photoId || !this.ready) {
      this.#autoAddedFor = -1;
      return;
    }
    const form = this.form;
    const button = form?.querySelector('.add-to-cart-button, [type="submit"][name="add"], [type="submit"]');
    form?.requestSubmit(button instanceof HTMLElement ? /** @type {any} */ (button) : undefined);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = this.form;
    if (!form || event.target !== form) return;

    // already uploading and adding: ignore a second tap or Enter so the charm is not added twice
    if (this.#uploading && !this.#resubmit) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const resubmitted = this.#resubmit;
    this.#resubmit = false;

    if (!this.ready) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#needed = true;
      this.classList.remove('is-needed');
      void this.offsetWidth; // restart the nudge animation
      this.classList.add('is-needed');
      this.status.textContent = this.#img
        ? 'Please tick the box to confirm your photo before adding to cart.'
        : 'Please add your photo before adding to cart.';
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (this.#img ? this.confirm : this.fileInput)?.focus({ preventScroll: true });
      return;
    }

    // Let the customer know the wait is the photo uploading
    this.#showUploading();

    // The newest crop is still being saved: wait for it, then add to cart
    if (!resubmitted && (this.#dirty || this.#saving)) {
      const pending = this.#dirty ? this.#saveCrop() : this.#saving;
      if (pending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const submitter = event.submitter;
        pending.then(() => {
          this.#saving = null;
          this.#resubmit = true;
          form.requestSubmit(submitter instanceof HTMLElement ? /** @type {any} */ (submitter) : undefined);
        });
      }
    }
  };

  /**
   * While the photo uploads with Add to cart: a large banner in the builder and the Add to cart button itself say so,
   * so nobody thinks it did not work. When the cart answers, the banner says it was added (or what went wrong).
   */
  #showUploading() {
    if (this.#uploading) return;
    this.#uploading = true;
    const buttons = this.form?.closest('product-form-component')?.querySelector('.product-form-buttons');
    this.classList.add('is-uploading');
    this.classList.remove('is-added');
    buttons?.classList.add('rpb-uploading');
    this.status.classList.remove('is-ready');
    this.status.innerHTML =
      '<span class="rpb__spin" aria-hidden="true"></span><span><b>Uploading your photo…</b><small>This takes a few seconds. Please keep this page open.</small></span><i class="rpb__load" aria-hidden="true"></i>';
    this.status.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    const finish = (/** @type {boolean} */ ok) => {
      if (!this.#uploading) return;
      this.#uploading = false;
      clearTimeout(timer);
      this.classList.remove('is-uploading');
      buttons?.classList.remove('rpb-uploading');
      if (ok) {
        this.classList.add('is-added');
        this.status.innerHTML =
          '<span class="rpb__done" aria-hidden="true"><svg viewBox="0 0 24 24" width="16" height="16"><path d="M5 12l5 5 9-10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg></span><span><b>Added to your cart!</b><small>Your photo is attached to this charm.</small></span>';
        setTimeout(() => {
          this.classList.remove('is-added');
          this.#update();
        }, 5000);
      } else {
        this.#update();
        this.status.textContent = 'Something went wrong adding your charm. Please try again.';
      }
    };
    const onAdd = (/** @type {any} */ event) => {
      document.removeEventListener('shopify:cart:lines-update', onAdd);
      Promise.resolve(event.promise).then(
        (/** @type {any} */ result) => finish(!result?.detail?.didError),
        () => finish(false)
      );
    };
    document.addEventListener('shopify:cart:lines-update', onAdd);
    const timer = setTimeout(() => finish(true), 45000); // never leave the message up
  }

  /** @param {PointerEvent} event */
  #onDown = (event) => {
    if (!this.#img) return;
    this.stage.setPointerCapture(event.pointerId);
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.#pointers.size === 2) {
      const [a, b] = [...this.#pointers.values()];
      this.#pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom: this.#zoom };
    }
  };

  /** @param {PointerEvent} event */
  #onMove = (event) => {
    const last = this.#pointers.get(event.pointerId);
    if (!last || !this.#img) return;
    const now = { x: event.clientX, y: event.clientY };
    this.#pointers.set(event.pointerId, now);
    if (this.#pointers.size === 2) {
      const [a, b] = [...this.#pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (this.#pinch.dist) this.#setZoom(this.#pinch.zoom * (dist / this.#pinch.dist));
      return;
    }
    this.#ox += now.x - last.x;
    this.#oy += now.y - last.y;
    this.#clamp();
    this.#render(true);
  };

  /** @param {PointerEvent} event */
  #onUp = (event) => {
    this.#pointers.delete(event.pointerId);
    if (this.#pointers.size < 2) this.#pinch.dist = 0;
  };

  /** @param {KeyboardEvent} event */
  #onKey = (event) => {
    if (!this.#img) return;
    const step = event.shiftKey ? 20 : 5;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (event.key in moves) {
      event.preventDefault();
      const [dx, dy] = moves[/** @type {keyof typeof moves} */ (event.key)];
      this.#ox += dx;
      this.#oy += dy;
      this.#clamp();
      this.#render(true);
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      this.#setZoom(this.#zoom + 0.1);
    } else if (event.key === '-') {
      event.preventDefault();
      this.#setZoom(this.#zoom - 0.1);
    }
  };
}

if (!customElements.get('rs-photo-builder')) customElements.define('rs-photo-builder', RsPhotoBuilder);
